import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, AlertTriangle, Scale, CreditCard, Ban, RefreshCw, Globe, Gavel } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            Volver
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            <span className="font-semibold text-gray-900">Grupo Ibérica</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terminos de Servicio</h1>
            <p className="text-gray-500">Ultima actualizacion: 22 de enero de 2026</p>
            <p className="text-gray-500">Version: 1.0</p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {/* 1. Objeto */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">1. Objeto del Servicio</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  Sistema de Multas es una plataforma de software como servicio (SaaS) que proporciona
                  herramientas para la gestion automatizada de multas de trafico para flotas de vehiculos.
                </p>
                <p>El servicio incluye:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Recepcion y centralizacion de notificaciones de multas</li>
                  <li>Identificacion automatica de conductores</li>
                  <li>Comunicacion con organismos sancionadores (DGT, ayuntamientos)</li>
                  <li>Generacion de informes y estadisticas</li>
                  <li>Gestion de recursos y alegaciones (segun plan contratado)</li>
                </ul>
              </div>
            </section>

            {/* 2. Aceptacion */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">2. Aceptacion de los Terminos</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  Al registrarse, acceder o utilizar Sistema de Multas, usted acepta quedar vinculado por estos
                  Terminos de Servicio. Si no esta de acuerdo con alguna parte, no podra utilizar el servicio.
                </p>
                <p>
                  Estos terminos constituyen un acuerdo legalmente vinculante entre usted (el "Usuario"
                  o la "Empresa") y el prestador del servicio.
                </p>
              </div>
            </section>

            {/* 3. Registro */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">3. Registro y Cuenta</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>Para utilizar el servicio debe:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Proporcionar informacion veraz y actualizada</li>
                  <li>Ser mayor de edad y tener capacidad legal para contratar</li>
                  <li>Mantener la confidencialidad de sus credenciales de acceso</li>
                  <li>Notificar inmediatamente cualquier uso no autorizado de su cuenta</li>
                </ul>
                <p className="mt-2">
                  Usted es responsable de todas las actividades que ocurran bajo su cuenta.
                </p>
              </div>
            </section>

            {/* 4. Periodo de Prueba */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">4. Periodo de Prueba</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  Ofrecemos un periodo de prueba gratuita de 14 dias naturales. Durante este periodo:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Tendra acceso completo a las funcionalidades del plan basico</li>
                  <li>No se requiere tarjeta de credito para iniciar la prueba</li>
                  <li>Al finalizar, debera contratar un plan para continuar usando el servicio</li>
                  <li>Los datos introducidos durante la prueba se conservaran al contratar</li>
                </ul>
              </div>
            </section>

            {/* 5. Precios y Pagos */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">5. Precios y Facturacion</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>Los precios del servicio se establecen segun el plan contratado:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Los precios no incluyen IVA salvo indicacion expresa</li>
                  <li>La facturacion se realiza mensualmente por uso</li>
                  <li>El pago se efectuara mediante domiciliacion bancaria o tarjeta</li>
                  <li>Las facturas impagadas podran suponer la suspension del servicio</li>
                </ul>
                <p className="mt-2">
                  Nos reservamos el derecho de modificar los precios con preaviso de 30 dias.
                </p>
              </div>
            </section>

            {/* 6. Uso Aceptable */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">6. Uso Aceptable</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>El usuario se compromete a NO:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Usar el servicio para actividades ilegales o fraudulentas</li>
                  <li>Introducir datos falsos o de terceros sin autorizacion</li>
                  <li>Intentar acceder a sistemas o datos de otros usuarios</li>
                  <li>Realizar ingenieria inversa o modificar el software</li>
                  <li>Revender o sublicenciar el acceso al servicio</li>
                  <li>Sobrecargar intencionadamente los sistemas</li>
                </ul>
              </div>
            </section>

            {/* 7. Limitacion Responsabilidad */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Ban className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">7. Limitacion de Responsabilidad</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  El servicio se proporciona "tal cual" y "segun disponibilidad".
                  No garantizamos que el servicio sea ininterrumpido o libre de errores.
                </p>
                <p className="font-semibold mt-2">No seremos responsables de:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Danos indirectos, incidentales o consecuentes</li>
                  <li>Perdida de datos no causada por negligencia nuestra</li>
                  <li>Retrasos causados por terceros (DGT, organismos publicos)</li>
                  <li>Sanciones o recargos por incumplimiento de plazos del usuario</li>
                  <li>Errores en datos proporcionados por el usuario</li>
                </ul>
                <p className="mt-2">
                  Nuestra responsabilidad maxima se limita al importe pagado en los ultimos 12 meses.
                </p>
              </div>
            </section>

            {/* 8. Propiedad Intelectual */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">8. Propiedad Intelectual</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  Sistema de Multas y todo su contenido (software, diseno, logos, textos) son propiedad
                  exclusiva del prestador y estan protegidos por las leyes de propiedad intelectual.
                </p>
                <p>
                  El usuario conserva la propiedad de los datos que introduce en la plataforma.
                </p>
              </div>
            </section>

            {/* 9. Resolucion */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-gray-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">9. Duracion y Resolucion</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  El contrato tiene duracion indefinida y puede ser cancelado por cualquiera de las partes:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>El usuario puede cancelar en cualquier momento desde su panel</li>
                  <li>La cancelacion sera efectiva al final del periodo facturado</li>
                  <li>Los datos se conservaran 30 dias tras la cancelacion para su exportacion</li>
                  <li>Podemos suspender cuentas por incumplimiento de estos terminos</li>
                </ul>
              </div>
            </section>

            {/* 10. Legislacion */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <Gavel className="w-5 h-5 text-pink-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">10. Legislacion Aplicable</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  Estos terminos se rigen por la legislacion espanola y el Derecho de la Union Europea.
                </p>
                <p>
                  Normativa aplicable:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Reglamento General de Proteccion de Datos (RGPD)</li>
                  <li>Ley Organica 3/2018 de Proteccion de Datos (LOPDGDD)</li>
                  <li>Ley 34/2002 de Servicios de la Sociedad de la Informacion (LSSI)</li>
                  <li>Texto Refundido de la Ley General de Consumidores</li>
                </ul>
                <p className="mt-2">
                  Para cualquier controversia, las partes se someten a los Juzgados y Tribunales
                  del domicilio del consumidor o, en su caso, de la sede del prestador.
                </p>
              </div>
            </section>

            {/* 11. Modificaciones */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-cyan-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">11. Modificacion de los Terminos</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  Nos reservamos el derecho de modificar estos terminos. Las modificaciones
                  sustanciales se comunicaran con al menos 30 dias de antelacion.
                </p>
                <p>
                  El uso continuado del servicio tras la notificacion implica la aceptacion
                  de los nuevos terminos.
                </p>
              </div>
            </section>

            {/* 12. Contacto */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">12. Contacto</h2>
              </div>
              <div className="pl-13 space-y-2 text-gray-600">
                <p>
                  Para cualquier consulta relacionada con estos Terminos de Servicio, puede contactarnos en:
                </p>
                <p className="mt-2">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:legal@multaflow.com" className="text-blue-600 hover:underline">
                    legal@multaflow.com
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t text-center text-gray-500 text-sm">
            <p>Sistema de Multas - Grupo Ibérica. Todos los derechos reservados</p>
            <p className="mt-2">
              <Link to="/privacidad" className="hover:underline">Privacidad</Link>
              {' · '}
              <Link to="/terminos" className="hover:underline">Terminos</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
